import {Badge} from '@mantine/core';
import {Box} from 'src/design/atoms/Box';
import {Center} from 'src/design/atoms/Center';
import {Divider} from 'src/design/atoms/Divider';
import {Group} from 'src/design/atoms/Group';
import {SimpleGrid} from 'src/design/atoms/SimpleGrid';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {ThemeIcon} from 'src/design/atoms/ThemeIcon';
import {Tooltip} from 'src/design/atoms/Tooltip';
import {
  IconCalendarClock,
  IconClockExclamation,
  IconHourglass,
  IconId,
} from '@tabler/icons-react';
import dayjs from 'dayjs';
import React, {useState} from 'react';
import {Controller, useForm} from 'react-hook-form';
import type {PersonnelResponse} from 'src/api/ProactApiClient';
import {FeatureFlagEnum, UserTypeEnum} from 'src/api/ProactApiClient';
import {Button} from 'src/design/atoms/Button';
import {DatePicker} from 'src/design/atoms/DatePicker';
import {SectionCard} from 'src/design/atoms/SectionCard';
import {StatTile} from 'src/design/molecules/StatTile';
import {PrintBadgeSection} from 'src/app/personnel/badge/PrintBadgeSection';
import {usePersonnelPermissions} from 'src/shared/auth/permissions/usePersonnelPermissions';
import {useAlert} from 'src/shared/hooks/useAlert';
import useActiveFeatureFlags from 'src/shared/hooks/useActiveFeatureFlags';
import {getDaysDiff} from 'src/shared/utils/formatters';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {parseUtcDate} from 'src/shared/utils/parseUtcDate';
import {
  useExtendBadgeExpirationMutation,
  useSetBadgeExpirationMutation,
} from 'src/redux/queries/personnel';

const EXTEND_OPTIONS = [1, 3, 7, 30] as const;

type ExtendAction = (typeof EXTEND_OPTIONS)[number] | 'set' | 'clear';

export const getDaysRemainingColor = (
  daysRemaining: number | null
): string => {
  if (daysRemaining === null) return 'gray';
  if (daysRemaining <= 2) return 'red';
  if (daysRemaining <= 7) return 'yellow';
  return 'gray';
};

export interface PersonnelBadgeAccessTabProps {
  item?: PersonnelResponse;
  onItemUpdated: (item: PersonnelResponse) => void;
}

interface BadgeAccessFormValues {
  expirationDate: Date | null;
}

export const PersonnelBadgeAccessTab: React.FC<
  PersonnelBadgeAccessTabProps
> = ({item, onItemUpdated}) => {
  const {setAlert} = useAlert();
  const personnelPerms = usePersonnelPermissions();
  const [isUpdating, setIsUpdating] = useState<ExtendAction | null>(null);

  const [extendBadgeExpiration] = useExtendBadgeExpirationMutation();
  const [setBadgeExpiration] = useSetBadgeExpirationMutation();

  const {
    control,
    handleSubmit: handleFormSubmit,
    reset,
    watch,
  } = useForm<BadgeAccessFormValues>({
    defaultValues: {
      expirationDate: item?.badgeExpirationAt
        ? parseUtcDate(item.badgeExpirationAt)
        : null,
    },
  });

  const selectedDate = watch('expirationDate');

  const activeFeatureFlags = useActiveFeatureFlags();
  const isBadgePrintingEnabled =
    activeFeatureFlags?.includes(FeatureFlagEnum.BadgePrinting) === true;

  if (!item) return null;

  const isContractor = item.userType === UserTypeEnum.Contractor;
  const showPrintBadgeSection = isBadgePrintingEnabled && personnelPerms.printBadge;
  const showBadgeExpirationSection =
    isContractor &&
    (personnelPerms.badgeAccess || personnelPerms.badgeAccessAdmin);

  const badgeExpiration = item.badgeExpirationAt
    ? parseUtcDate(item.badgeExpirationAt)
    : null;

  const isExpired = badgeExpiration !== null && badgeExpiration < new Date();
  const isNull = !badgeExpiration;

  const handleExtend = async (days: (typeof EXTEND_OPTIONS)[number]) => {
    if (!item.id) return;
    try {
      setIsUpdating(days);
      const resp = await extendBadgeExpiration({id: item.id, model: {days}}).unwrap();
      onItemUpdated(resp);
      reset({
        expirationDate: resp.badgeExpirationAt
          ? parseUtcDate(resp.badgeExpirationAt)
          : null,
      });
      setAlert(`Badge expiration extended by ${days} day(s).`, 'success');
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    } finally {
      setIsUpdating(null);
    }
  };

  const onSetDate = async (values: BadgeAccessFormValues) => {
    if (!item.id || !values.expirationDate) return;
    try {
      setIsUpdating('set');
      const resp = await setBadgeExpiration({
        id: item.id,
        model: {badgeExpirationAt: values.expirationDate},
      }).unwrap();
      onItemUpdated(resp);
      reset({
        expirationDate: resp.badgeExpirationAt
          ? parseUtcDate(resp.badgeExpirationAt)
          : null,
      });
      setAlert('Badge expiration date updated.', 'success');
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    } finally {
      setIsUpdating(null);
    }
  };

  const handleClear = async () => {
    if (!item.id) return;
    try {
      setIsUpdating('clear');
      const resp = await setBadgeExpiration({
        id: item.id,
        model: {badgeExpirationAt: undefined},
      }).unwrap();
      onItemUpdated(resp);
      reset({expirationDate: null});
      setAlert('Badge expiration cleared.', 'success');
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    } finally {
      setIsUpdating(null);
    }
  };

  const statusLabel = isNull ? 'Not tracked' : isExpired ? 'Expired' : 'Active';
  const statusColor = isNull ? 'gray' : isExpired ? 'red' : 'green';
  const expirationDisplay = badgeExpiration
    ? dayjs(badgeExpiration).format('MM/DD/YYYY')
    : '—';
  const daysRemaining =
    badgeExpiration && !isExpired
      ? -getDaysDiff(badgeExpiration)
      : null;
  const daysRemainingDisplay =
    daysRemaining !== null ? `${daysRemaining}` : '—';

  return (
    <Stack gap="lg">
      {showBadgeExpirationSection && (
        <SectionCard
          icon={<IconClockExclamation size={16} />}
          title="Badge Expiration"
          rightSection={
            <Badge color={statusColor} variant="light">
              {statusLabel}
            </Badge>
          }>
          <Box p="md">
            <Stack gap="lg">
              <SimpleGrid cols={{base: 1, xs: 2}}>
                <StatTile
                  title="Expires"
                  value={expirationDisplay}
                  icon={<IconCalendarClock size={20} />}
                  iconColor={statusColor}
                />
                <StatTile
                  title="Days Remaining"
                  value={daysRemainingDisplay}
                  icon={<IconHourglass size={20} />}
                  iconColor={getDaysRemainingColor(daysRemaining)}
                />
              </SimpleGrid>

              <Text size="sm" c="dimmed">
                Expiration deactivates the contractor's account; extending or
                setting a date reactivates them.
              </Text>

              <Divider />

              <Stack gap="sm">
                <Text fw={600} size="sm" c="dimmed">
                  Quick Extend
                </Text>
                {isNull ? (
                  <Text size="sm" c="dimmed">
                    {personnelPerms.badgeAccessAdmin
                      ? 'Set an expiration date to start tracking.'
                      : 'No expiration is set for this contractor.'}
                  </Text>
                ) : (
                  <Group gap="sm">
                    {EXTEND_OPTIONS.map(days => (
                      <Button
                        key={days}
                        size="sm"
                        variant="default"
                        disabled={isUpdating !== null}
                        loading={isUpdating === days}
                        onClick={() => handleExtend(days)}>
                        +{days} {days === 1 ? 'day' : 'days'}
                      </Button>
                    ))}
                  </Group>
                )}
              </Stack>

              {personnelPerms.badgeAccessAdmin && (
                <>
                  <Divider />
                  <Stack gap={0}>
                    <Text fw={600} size="sm" c="dimmed" mb="xs">
                      Set Expiration Date
                    </Text>
                    <Controller
                      name="expirationDate"
                      control={control}
                      render={({field}) => (
                        <DatePicker
                          label=""
                          ariaLabel="Expiration date"
                          value={field.value}
                          onDateChangeCallback={value => field.onChange(value)}
                          w="fit-content"
                        />
                      )}
                    />
                    <Group gap="sm" align="flex-end">
                      <Button
                        size="sm"
                        disabled={!selectedDate || isUpdating !== null}
                        loading={isUpdating === 'set'}
                        onClick={handleFormSubmit(onSetDate)}>
                        Save
                      </Button>
                      <Tooltip label="Clearing stops expiration tracking">
                        <Button
                          size="sm"
                          variant="subtle"
                          color="red"
                          disabled={isNull || isUpdating !== null}
                          loading={isUpdating === 'clear'}
                          onClick={handleClear}>
                          Clear
                        </Button>
                      </Tooltip>
                    </Group>
                  </Stack>
                </>
              )}
            </Stack>
          </Box>
        </SectionCard>
      )}

      {showPrintBadgeSection && <PrintBadgeSection personnelId={item.id} />}

      {!showBadgeExpirationSection && !showPrintBadgeSection && (
        <Center py="xl">
          <Stack align="center" gap="sm">
            <ThemeIcon size={48} radius="xl" variant="light" color="gray">
              <IconId size={24} />
            </ThemeIcon>
            <Text c="dimmed" size="sm">
              Nothing to show — this person has no badge expiration to manage
              and badge printing is not available to you.
            </Text>
          </Stack>
        </Center>
      )}
    </Stack>
  );
};
